<template>
  <div class="forum_messages">
    <div class="forum_messages__header">
      <button type="button" class="forum_messages__back" @click="goBack">‹</button>
      <span>消息中心</span>
      <button
        type="button"
        class="forum_messages__read_all"
        :disabled="readAllRequesting || !hasUnreadMessage"
        @click="onReadAll"
      >全部已读</button>
    </div>
    <div class="forum_messages__tab">论坛消息</div>
    <van-list v-model="loading" :finished="finished" :immediate-check="false" :finished-text="messageFinishedText" @load="fetchPage(false)">
      <div
        v-for="item in list"
        :key="getMessageKey(item)"
        :class="['message_item', { 'message_item--read': isMessageRead(item), 'message_item--unread': isMessageUnread(item) }]"
        @click="onClick(item)"
      >
        <i v-if="isMessageUnread(item)" class="message_item__dot" />
        <div class="message_item__avatar">
          <img :src="getMessageAvatar(item)" alt="" />
        </div>
        <div class="message_item__body">
          <div class="message_item__top">
            <div class="message_item__title">
              <span>{{ getMessageTitle(item) }}</span>
            </div>
            <em>{{ item.timeDesc }}</em>
          </div>
          <div class="message_item__content">{{ getMessageContent(item) }}</div>
        </div>
      </div>
    </van-list>
    <EmptyState v-if="!loading && list.length === 0" text="暂无消息" />
  </div>
</template>

<script>
import { List } from 'vant';
import {
  getCommunityMsgList,
  getCommunityUnreadMsgOverview,
  readAllCommunityMsg,
  readCommunityMsg,
} from '@/api/forum';
import { getForumMessageTargetQuery } from '@/utils/forum/message';
import { FORUM_DEFAULT_AVATAR } from './constants';
import EmptyState from './components/EmptyState.vue';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import forumLoginRefreshMixin from './mixins/forumLoginRefresh';

export default {
  name: 'ForumMessages',
  mixins: [trackingMixin, forumLoginRefreshMixin],
  components: { [List.name]: List, EmptyState },
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      list: [],
      page: 1,
      pageSize: 20,
      finished: false,
      loading: false,
      messageRequesting: false,
      skipInitialActivated: false,
      unreadCount: 0,
      readingMap: {},
      readAllRequesting: false,
    };
  },
  computed: {
    messageFinishedText() {
      return Array.isArray(this.list) && this.list.length > 0 ? '没有更多了' : '';
    },
    hasUnreadMessage() {
      return Number(this.unreadCount || 0) > 0 || this.list.some((item) => this.isMessageUnread(item));
    },
  },
  activated() {
    if (this.skipInitialActivated) {
      this.skipInitialActivated = false;
      return;
    }
    this.fetchPage(true);
    this.trackPageShow();
  },
  created() {
    this.skipInitialActivated = true;
    this.fetchPage(true);
    this.trackPageShow();
  },
  methods: {
    getForumLoginRefreshConfig() {
      return {
        routeName: 'forum-messages',
        onRefresh: () => this.fetchPage(true),
      };
    },
    trackPageShow() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
        page_type: '黑喵消息中心',
      });
    },
    getMessageRecords(data) {
      if (data && Array.isArray(data.list)) return data.list;
      return [];
    },
    goBack() {
      this.$router.back();
    },
    getMessageKey(item) {
      return item.id;
    },
    getMessageAvatar(item) {
      return item.avatar || FORUM_DEFAULT_AVATAR;
    },
    getMessageTitle(item) {
      if (Number(item.type) === 3) return '系统通知';
      const actionMap = {
        0: '点赞了你的帖子',
        1: '评论了你的帖子',
        2: '回复了你的评论',
        4: '收藏了你的帖子',
        5: '点赞了你的评论',
      };
      const name = item.nickname || '喵友';
      return `${name} ${actionMap[Number(item.type)] || '与你互动'}`;
    },
    getMessageContent(item) {
      if (Number(item.type) === 3) {
        return item.title ? `你的帖子「${item.title}」被选为精华贴，经验+10` : '你的帖子被选为精华贴，经验+10';
      }
      if (Number(item.type) === 1 || Number(item.type) === 5) return item.comment || '';
      if (Number(item.type) === 2) return item.replyComment || '';
      return item.comment || item.replyComment || item.content || item.title || '';
    },
    async fetchUnreadCount() {
      const res = await getCommunityUnreadMsgOverview();
      this.unreadCount = Number(res && res.data && res.data.total) || 0;
    },
    getMessageReadState(item) {
      if (!item) return 'unknown';
      if (item.__forumRead === true) return 'read';
      if (Number(item.readStatus) === 2) return 'read';
      if (Number(item.readStatus) === 1) return 'unread';
      return 'unknown';
    },
    isMessageRead(item) {
      return item && item.readStatus === 2;
    },
    isMessageUnread(item) {
      return item && item.readStatus === 1;
    },
    isReadResponseOk(res) {
      return Boolean(res && res.data === true);
    },
    markMessageRead(item) {
      if (!item) return;
      this.$set(item, 'readStatus', 2);
    },
    markAllMessagesRead() {
      this.list.forEach((item) => {
        this.markMessageRead(item);
      });
    },
    async readMessage(item) {
      const msgId = item.id;
      if (this.isMessageRead(item) || this.readingMap[msgId]) return;
      this.$set(this.readingMap, msgId, true);
      try {
        const res = await readCommunityMsg(msgId);
        if (this.isReadResponseOk(res)) {
          this.markMessageRead(item);
          this.fetchUnreadCount();
        }
      } catch (error) {
        // 接口失败提示由 axios 统一处理，不阻断消息跳转。
      } finally {
        this.$delete(this.readingMap, msgId);
      }
    },
    async onReadAll() {
      if (this.readAllRequesting || !this.hasUnreadMessage) return;
      this.readAllRequesting = true;
      try {
        const res = await readAllCommunityMsg();
        if (this.isReadResponseOk(res)) {
          this.markAllMessagesRead();
          this.unreadCount = 0;
        }
      } catch (error) {
        // 接口失败提示由 axios 统一处理。
      } finally {
        this.readAllRequesting = false;
      }
    },
    async fetchPage(reset = false) {
      if (this.messageRequesting) return;
      if (reset) {
        this.list = [];
        this.page = 1;
        this.finished = false;
        this.fetchUnreadCount();
      }
      if (this.finished) {
        this.loading = false;
        return;
      }
      this.messageRequesting = true;
      this.loading = true;
      try {
        const res = await getCommunityMsgList({ page: this.page, size: this.pageSize });
        const data = res.data || {};
        const records = this.getMessageRecords(data);
        this.list.push(...records);
        const total = Number(data.total) || 0;
        if (!records.length || (total > 0 && this.list.length >= total) || records.length < this.pageSize) {
          this.finished = true;
        } else {
          this.page += 1;
        }
      } finally {
        this.messageRequesting = false;
        this.loading = false;
      }
    },
    async onClick(item) {
      await this.readMessage(item);
      if (!item.postId) return;
      this.$router.push({
        name: 'forum-post-detail',
        params: { postId: item.postId },
        query: getForumMessageTargetQuery(item),
      });
    },
  },
};
</script>

<style scoped>
.forum_messages {
  min-height: 100vh;
  background: #f9fafb;
}

.forum_messages__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 14px;
  background: #fff;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.forum_messages__back,
.forum_messages__read_all {
  height: 28px;
  border: 0;
  background: transparent;
}

.forum_messages__back {
  width: 58px;
  color: #333;
  font-size: 30px;
  line-height: 26px;
  text-align: left;
}

.forum_messages__read_all {
  width: 58px;
  color: #fd383c;
  font-size: 12px;
  text-align: right;
}

.forum_messages__read_all:disabled {
  color: #b8b8b8;
}

.forum_messages__tab {
  display: inline-flex;
  margin: 12px 0 4px 12px;
  padding-bottom: 6px;
  border-bottom: 2px solid #fd383c;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.message_item {
  position: relative;
  display: flex;
  margin: 8px 12px 0;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
}

.message_item__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  flex-shrink: 0;
}

.message_item__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message_item__body {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
}

.message_item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #333;
  font-size: 13px;
}

.message_item__title {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.message_item__title span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.message_item__dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fd383c;
}

.message_item__top em {
  flex-shrink: 0;
  margin-left: 8px;
  color: #aaa;
  font-style: normal;
  font-size: 10px;
}

.message_item__content {
  margin-top: 5px;
  color: #666;
  font-size: 12px;
  line-height: 18px;
}

.message_item--read .message_item__title,
.message_item--read .message_item__content {
  color: #9ca3af;
}
</style>
